import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Lock, LogOut, Loader2, Inbox, PhoneCall, Mail, Building2,
  MessageSquare, Users, MapPin, RefreshCw, Clock,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "quadrim_admin_token";

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [creds, setCreds] = useState({ email: "", password: "" });
  const [loginErr, setLoginErr] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);

  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState("");

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setLeads([]);
    setQuotes([]);
  }, []);

  const load = useCallback(async (t) => {
    setLoading(true);
    setLoadErr("");
    try {
      const headers = { Authorization: `Bearer ${t}` };
      const [l, q] = await Promise.all([
        axios.get(`${API}/admin/leads`, { headers }),
        axios.get(`${API}/admin/quotes`, { headers }),
      ]);
      setLeads(l.data);
      setQuotes(q.data);
    } catch (e) {
      if (e.response?.status === 401) {
        logout();
        setLoginErr("Session expired. Please sign in again.");
      } else {
        setLoadErr("Could not load the inbox. Try refreshing.");
      }
    } finally {
      setLoading(false);
    }
  }, [logout]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const submitLogin = async (e) => {
    e.preventDefault();
    setLoginErr("");
    setLoggingIn(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, creds);
      localStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setLoginErr(typeof d === "string" ? d : "Invalid email or password");
    } finally {
      setLoggingIn(false);
    }
  };

  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString(undefined, {
        day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  // ---- Login screen ----
  if (!token) {
    return (
      <div data-testid="admin-login" className="min-h-[80vh] flex items-center justify-center px-6 pt-28 pb-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          className="w-full max-w-md rounded-2xl border border-white/10 bg-surface p-8"
        >
          <div className="grid h-12 w-12 place-items-center rounded-full border border-white/10 bg-[#0A0A0B] mb-6">
            <Lock className="h-5 w-5 text-accent" />
          </div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-2">Quadrim Tech</p>
          <h1 className="font-heading text-3xl font-medium tracking-tight mb-6">Inbox sign-in</h1>
          <form onSubmit={submitLogin} noValidate className="space-y-4">
            <div>
              <span className="block font-mono text-[11px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-2">Email</span>
              <input
                data-testid="admin-email-input"
                type="email"
                value={creds.email}
                onChange={(e) => { setCreds((c) => ({ ...c, email: e.target.value })); if (loginErr) setLoginErr(""); }}
                placeholder="admin@quadrimtech.com"
                className="lead-input"
              />
            </div>
            <div>
              <span className="block font-mono text-[11px] uppercase tracking-[0.15em] text-[#A1A1AA] mb-2">Password</span>
              <input
                data-testid="admin-password-input"
                type="password"
                value={creds.password}
                onChange={(e) => { setCreds((c) => ({ ...c, password: e.target.value })); if (loginErr) setLoginErr(""); }}
                placeholder="••••••••"
                className="lead-input"
              />
            </div>
            {loginErr && <p data-testid="admin-login-error" className="text-sm text-red-400">{loginErr}</p>}
            <button
              data-testid="admin-login-submit"
              type="submit"
              disabled={loggingIn}
              className="w-full flex items-center justify-center gap-3 rounded-full bg-accent text-black px-6 py-4 font-mono text-xs uppercase tracking-[0.15em] hover:bg-white transition-colors duration-300 disabled:opacity-60"
            >
              {loggingIn ? <><Loader2 className="h-4 w-4 animate-spin" /> Signing in</> : "Sign in"}
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  // ---- Inbox screen ----
  const items = tab === "leads" ? leads : quotes;

  return (
    <div data-testid="admin-inbox" className="min-h-[80vh] px-6 md:px-10 pt-28 pb-20">
      <div className="mx-auto max-w-[1100px]">
        <div className="flex items-center justify-between gap-4 mb-8">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-2">Quadrim Tech</p>
            <h1 className="font-heading text-4xl md:text-5xl font-medium tracking-tight flex items-center gap-3">
              <Inbox className="h-8 w-8 text-accent" /> Inbox
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button data-testid="admin-refresh" onClick={() => load(token)} className="grid h-11 w-11 place-items-center rounded-full border border-white/15 text-[#A1A1AA] hover:text-white hover:border-accent transition-colors duration-300" aria-label="Refresh">
              <RefreshCw className={`h-5 w-5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2.5 font-mono text-xs uppercase tracking-[0.15em] text-[#A1A1AA] hover:text-white hover:border-accent transition-colors duration-300">
              <LogOut className="h-4 w-4" /> Log out
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-8 border-b border-white/10">
          {[
            { id: "leads", label: "Enquiries", n: leads.length },
            { id: "quotes", label: "VoIP Quotes", n: quotes.length },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`admin-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`relative px-5 py-3 font-mono text-xs uppercase tracking-[0.15em] transition-colors duration-300 ${
                tab === t.id ? "text-white" : "text-[#71717A] hover:text-[#A1A1AA]"
              }`}
            >
              {t.label} <span className="text-accent">({t.n})</span>
              {tab === t.id && <span className="absolute left-0 -bottom-px h-0.5 w-full bg-accent" />}
            </button>
          ))}
        </div>

        {loadErr && <p data-testid="admin-load-error" className="text-sm text-red-400 mb-6">{loadErr}</p>}

        {loading && items.length === 0 ? (
          <div className="py-20 text-center text-[#71717A]"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
        ) : items.length === 0 ? (
          <div data-testid="admin-empty" className="py-20 text-center text-[#71717A] font-mono text-sm uppercase tracking-[0.15em]">
            No {tab === "leads" ? "enquiries" : "quotes"} yet.
          </div>
        ) : (
          <div className="space-y-4" data-testid={`admin-list-${tab}`}>
            {items.map((it) => (
              <motion.div
                key={it.id}
                data-testid={`admin-item-${it.id}`}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                className="rounded-2xl border border-white/10 bg-surface p-6"
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
                  <div>
                    <div className="font-heading text-xl font-medium tracking-tight">{it.name}</div>
                    <div className="flex flex-wrap items-center gap-x-5 gap-y-1 mt-2 text-sm text-[#A1A1AA]">
                      <a data-testid={`admin-item-email-${it.id}`} href={`mailto:${it.email}`} className="flex items-center gap-2 hover:text-white transition-colors"><Mail className="h-4 w-4 text-accent" />{it.email}</a>
                      {it.phone && <a data-testid={`admin-item-phone-${it.id}`} href={`tel:${it.phone}`} className="flex items-center gap-2 hover:text-white transition-colors"><PhoneCall className="h-4 w-4 text-accent" />{it.phone}</a>}
                      {it.company && <span className="flex items-center gap-2"><Building2 className="h-4 w-4 text-accent" />{it.company}</span>}
                    </div>
                  </div>
                  <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.12em] text-[#71717A] shrink-0">
                    <Clock className="h-3.5 w-3.5" />{fmt(it.created_at)}
                  </span>
                </div>

                {tab === "quotes" ? (
                  <div className="flex flex-wrap gap-3 mb-3">
                    <Chip icon={Users} label={`${it.users} users`} />
                    <Chip icon={MapPin} label={`${it.sites} sites`} />
                    {it.current_system && <Chip icon={PhoneCall} label={it.current_system} />}
                  </div>
                ) : (
                  it.service && <div className="mb-3"><Chip icon={MessageSquare} label={it.service} /></div>
                )}

                {it.message && (
                  <p className="text-[#A1A1AA] leading-relaxed border-t border-white/10 pt-4 whitespace-pre-wrap">
                    {it.message}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Chip({ icon: Icon, label }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-[#0A0A0B] px-3 py-1.5 text-sm text-[#F4F4F5]">
      <Icon className="h-4 w-4 text-accent" /> {label}
    </span>
  );
}
